import React from 'react';

import type { TooltipPlacement } from 'antd/es/tooltip';
import {
  Cable,
  CircleOff,
  Cloud,
  Download,
  FileText,
  FolderOpen,
  FolderTree,
  HardDrive,
  Heart,
  KeyRound,
  Layers,
  LayoutDashboard,
  Package,
  ShieldCheck,
  User,
  Users,
} from 'lucide-react';
import type { LucideIcon, LucideProps } from 'lucide-react';

import {
  AddIcon,
  ArrowDropDownIcon,
  ArrowShortDownIcon,
  ArrowShortUpIcon,
  ArtifactIcon,
  BoxIcon,
  CancelIcon,
  ChartBarIcon,
  ChartLineIcon,
  CheckCircleIcon,
  CheckIcon,
  CircleCheckIcon,
  ClockIcon,
  CollapseIcon,
  CompareIcon,
  ContainerIcon,
  CopyIcon,
  DagIcon,
  DatabaseIcon,
  DatabaseSchemaIcon,
  DatasetIcon,
  DeferredIcon,
  DeployedIcon,
  DomainNavIcon,
  EditIcon,
  ErrorIcon,
  ExpandIcon,
  ExperimentIcon,
  ExperimentsNavIcon,
  EyeOffIcon,
  EyeOnIcon,
  FailIcon,
  FeatureSetIcon,
  GemIcon,
  GlossaryTermIcon,
  HomeIcon,
  LightIcon,
  LightOnIcon,
  ListAlertIcon,
  MinusCircleIcon,
  MlModelIcon,
  NavCloseIcon,
  NavOpenIcon,
  NextIcon,
  OpenInNewIcon,
  ParameterSetIcon,
  PendingIcon,
  PipelineNavIcon,
  PlayIcon,
  PlusIcon,
  PrevIcon,
  QueueIcon,
  ReportIcon,
  ResetIcon,
  RunIcon,
  RunningCircleIcon,
  SaveIcon,
  ScheduleIcon,
  SearchNavIcon,
  ServerIcon,
  SettingIcon,
  SkipIcon,
  StarFilledIcon,
  StarIcon,
  StopIcon,
  StoredProcedureIcon,
  TableIcon,
  TableListIcon,
  TagIcon,
  TierIcon,
  TooltipIcon,
  TrashIcon,
  TreeCollapseIcon,
  TreeExpandIcon,
  UploadIcon,
  UpstreamFailIcon,
} from '@/shared/assets/icons';
import { Tooltip } from '@/shared/ui/Tooltip';

import type { AssetDetailType } from '@/entities/asset-detail/domain/types';

import styles from './Icon.module.css';

type SvgrIconComponent = React.FC<React.SVGProps<SVGSVGElement>>;

export type IconName =
  | 'add'
  | 'arrow-drop-down'
  | 'arrow-short-down'
  | 'arrow-short-up'
  | 'artifact'
  | 'book'
  | 'box'
  | 'cancel'
  | 'chart-bar'
  | 'chart-line'
  | 'check'
  | 'check-circle'
  | 'circle-check'
  | 'clock'
  | 'cloud'
  | 'collapse'
  | 'compare'
  | 'container'
  | 'copy'
  | 'dag'
  | 'dashboard'
  | 'data-product'
  | 'database'
  | 'database-schema'
  | 'dataset'
  | 'deferred'
  | 'deployed'
  | 'download'
  | 'edit'
  | 'error'
  | 'expand'
  | 'experiment'
  | 'experiments-nav'
  | 'eye-off'
  | 'eye-on'
  | 'fail'
  | 'feature-set'
  | 'file'
  | 'folder'
  | 'folder-tree'
  | 'foreign-key'
  | 'gem'
  | 'globe'
  | 'hard-drive'
  | 'heart'
  | 'home'
  | 'layers'
  | 'light'
  | 'light-on'
  | 'list-alert'
  | 'minus-circle'
  | 'ml-model'
  | 'nav-fold'
  | 'nav-unfold'
  | 'next'
  | 'not-null'
  | 'open-in-new'
  | 'parameter-set'
  | 'pending'
  | 'pipeline'
  | 'play'
  | 'plus-outlined'
  | 'prev'
  | 'primary-key'
  | 'queue'
  | 'report'
  | 'reset'
  | 'run'
  | 'running-circle'
  | 'save'
  | 'schedule'
  | 'search'
  | 'server'
  | 'setting'
  | 'shield-check'
  | 'skip'
  | 'star'
  | 'star-filled'
  | 'stop'
  | 'stored-procedure'
  | 'table'
  | 'table-list'
  | 'tag'
  | 'tier'
  | 'tooltip'
  | 'topic'
  | 'trash'
  | 'tree-collapse'
  | 'tree-expand'
  | 'upload'
  | 'upstream-fail'
  | 'user'
  | 'users';

export type AssetType =
  | AssetDetailType
  | 'dataset'
  | 'file'
  | 'glossaryTerm'
  | 'tag'
  | 'dataProduct'
  | 'experiment'
  | 'run'
  | 'feature_set'
  | 'parameter_set'
  | 'artifact';

type IconProps = Omit<LucideProps, 'ref'> & {
  name?: IconName;
  assetType?: AssetType | string;
  showTooltip?: boolean;
  tooltipTitle?: string;
  tooltipPlacement?: TooltipPlacement;
};

const ICON_MAP: Record<IconName, LucideIcon | SvgrIconComponent> = {
  add: AddIcon,
  'arrow-drop-down': ArrowDropDownIcon,
  'arrow-short-down': ArrowShortDownIcon,
  'arrow-short-up': ArrowShortUpIcon,
  artifact: ArtifactIcon,
  book: GlossaryTermIcon,
  box: BoxIcon,
  cancel: CancelIcon,
  'chart-bar': ChartBarIcon,
  'chart-line': ChartLineIcon,
  check: CheckIcon,
  'check-circle': CheckCircleIcon,
  'circle-check': CircleCheckIcon,
  clock: ClockIcon,
  cloud: Cloud,
  collapse: CollapseIcon,
  compare: CompareIcon,
  container: ContainerIcon,
  copy: CopyIcon,
  dag: DagIcon,
  dashboard: LayoutDashboard,
  'data-product': Package,
  database: DatabaseIcon,
  'database-schema': DatabaseSchemaIcon,
  dataset: DatasetIcon,
  deferred: DeferredIcon,
  deployed: DeployedIcon,
  download: Download,
  edit: EditIcon,
  error: ErrorIcon,
  expand: ExpandIcon,
  experiment: ExperimentIcon,
  'experiments-nav': ExperimentsNavIcon,
  'eye-off': EyeOffIcon,
  'eye-on': EyeOnIcon,
  fail: FailIcon,
  'feature-set': FeatureSetIcon,
  file: FileText,
  folder: FolderOpen,
  'folder-tree': FolderTree,
  'foreign-key': Cable,
  gem: GemIcon,
  globe: DomainNavIcon,
  'hard-drive': HardDrive,
  heart: Heart,
  home: HomeIcon,
  layers: Layers,
  light: LightIcon,
  'light-on': LightOnIcon,
  'list-alert': ListAlertIcon,
  'minus-circle': MinusCircleIcon,
  'ml-model': MlModelIcon,
  'nav-fold': NavOpenIcon,
  'nav-unfold': NavCloseIcon,
  next: NextIcon,
  'not-null': CircleOff,
  'open-in-new': OpenInNewIcon,
  'parameter-set': ParameterSetIcon,
  pending: PendingIcon,
  pipeline: PipelineNavIcon,
  play: PlayIcon,
  'plus-outlined': PlusIcon,
  prev: PrevIcon,
  'primary-key': KeyRound,
  queue: QueueIcon,
  report: ReportIcon,
  reset: ResetIcon,
  run: RunIcon,
  'running-circle': RunningCircleIcon,
  save: SaveIcon,
  schedule: ScheduleIcon,
  search: SearchNavIcon,
  server: ServerIcon,
  setting: SettingIcon,
  'shield-check': ShieldCheck,
  skip: SkipIcon,
  star: StarIcon,
  'star-filled': StarFilledIcon,
  stop: StopIcon,
  'stored-procedure': StoredProcedureIcon,
  table: TableIcon,
  'table-list': TableListIcon,
  tag: TagIcon,
  tier: TierIcon,
  tooltip: TooltipIcon,
  topic: FolderOpen,
  trash: TrashIcon,
  'tree-collapse': TreeCollapseIcon,
  'tree-expand': TreeExpandIcon,
  upload: UploadIcon,
  'upstream-fail': UpstreamFailIcon,
  user: User,
  users: Users,
};

const SVGR_ICONS = new Set<IconName>([
  'add',
  'arrow-drop-down',
  'arrow-short-down',
  'arrow-short-up',
  'artifact',
  'book',
  'box',
  'cancel',
  'chart-bar',
  'chart-line',
  'check',
  'check-circle',
  'circle-check',
  'clock',
  'collapse',
  'compare',
  'container',
  'copy',
  'dag',
  'database',
  'database-schema',
  'dataset',
  'deferred',
  'deployed',
  'edit',
  'error',
  'expand',
  'experiment',
  'experiments-nav',
  'eye-off',
  'eye-on',
  'fail',
  'feature-set',
  'gem',
  'globe',
  'home',
  'light',
  'light-on',
  'list-alert',
  'minus-circle',
  'ml-model',
  'nav-fold',
  'nav-unfold',
  'next',
  'open-in-new',
  'parameter-set',
  'pending',
  'pipeline',
  'play',
  'plus-outlined',
  'prev',
  'queue',
  'report',
  'reset',
  'run',
  'running-circle',
  'save',
  'schedule',
  'search',
  'server',
  'setting',
  'skip',
  'star',
  'star-filled',
  'stop',
  'stored-procedure',
  'table',
  'table-list',
  'tag',
  'tier',
  'tooltip',
  'trash',
  'tree-collapse',
  'tree-expand',
  'upload',
  'upstream-fail',
]);

const ASSET_TYPE_ICON_MAP: Record<AssetType, IconName> = {
  artifact: 'artifact',
  container: 'container',
  dashboard: 'dashboard',
  database: 'database',
  databaseSchema: 'database-schema',
  dataProduct: 'data-product',
  dataset: 'dataset',
  experiment: 'experiment',
  feature_set: 'feature-set',
  file: 'file',
  glossaryTerm: 'book',
  mlmodel: 'ml-model',
  parameter_set: 'parameter-set',
  pipeline: 'pipeline',
  run: 'run',
  table: 'table',
  tag: 'tag',
  topic: 'topic',
};

const DEFAULT_TOOLTIP_TITLES: Partial<Record<IconName, string>> = {
  container: 'Bucket',
  collapse: 'Collapse',
  dag: 'DAG',
  dashboard: 'Dashboard',
  'data-product': 'Data Product',
  database: 'Database',
  'database-schema': 'Database Schema',
  experiment: 'Experiment',
  expand: 'Expand',
  download: 'Download',
  file: 'File',
  folder: 'Folder',
  'foreign-key': 'Foreign Key',
  'ml-model': 'ML Model',
  'not-null': 'Not Null',
  pending: 'Pending',
  pipeline: 'Pipeline',
  'primary-key': 'Primary Key',
  run: 'Run',
  'running-circle': 'Running',
  save: 'Save',
  'stored-procedure': 'Stored Procedure',
  table: 'Table',
  'table-list': 'Table List',
  tag: 'Tag',
  topic: 'Topic',
};

const isAssetType = (type: string): type is AssetDetailType => {
  return Object.hasOwn(ASSET_TYPE_ICON_MAP, type);
};

export const Icon: React.FC<IconProps> = ({
  name,
  assetType,
  showTooltip = false,
  tooltipTitle,
  tooltipPlacement = 'top',
  size = 16,
  strokeWidth = 2,
  color,
  className,
  style,
  ...props
}) => {
  let iconName = name;
  let defaultTitle = tooltipTitle;

  if (assetType) {
    iconName = isAssetType(assetType) ? ASSET_TYPE_ICON_MAP[assetType] : 'file';
    defaultTitle = defaultTitle || assetType;
  }

  const IconComponent = (iconName && ICON_MAP[iconName]) || FileText;
  const isSvgrIcon = iconName && SVGR_ICONS.has(iconName);

  const normalizedSize = typeof size === 'string' ? parseInt(size, 10) : size;
  const normalizedStrokeWidth = typeof strokeWidth === 'string' ? parseInt(strokeWidth, 10) : strokeWidth;

  const iconElement = isSvgrIcon ? (
    <IconComponent
      width={normalizedSize}
      height={normalizedSize}
      className={className}
      style={{ color, ...style }}
      {...props}
    />
  ) : (
    <IconComponent
      size={normalizedSize}
      strokeWidth={normalizedStrokeWidth}
      color={color}
      className={className}
      style={style}
      {...props}
    />
  );

  if (!showTooltip) {
    return iconElement;
  }

  const title = tooltipTitle || defaultTitle || (iconName && DEFAULT_TOOLTIP_TITLES[iconName]) || iconName;

  return (
    <Tooltip title={title} placement={tooltipPlacement}>
      <span className={styles.tooltipWrapper}>{iconElement}</span>
    </Tooltip>
  );
};
